/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { useState, useEffect } from "react";
import { Paper, Typography, Alert, Snackbar } from "@mui/material";
import {
  useGameConfigService,
  IIdentifiableStaticData,
  IDiffResponse,
  IDiffItem,
} from "../../services/backend/GameConfigService";
import { LoadingOverlay } from "../../components/LoadingOverlay";
import { YesNoCancelPopup } from "../../components/YesNoCancelPopup";
import { GameConfigItemsTable } from "./GameConfigItemsTable";
import JsonViewer from "../../components/JSONViewer";
import JSONDiff from "../../components/JSONDiff";

export interface ItemsManagementTabPanelProps {
  selectedBranch: string | undefined;
}

export const ItemsManagementTabPanel = ({ selectedBranch }: ItemsManagementTabPanelProps) => {
  const gameConfigService = useGameConfigService();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<"success" | "error" | "info" | "warning">("info");
  const [items, setItems] = useState<IIdentifiableStaticData[]>([]);
  const [diffData, setDiffData] = useState<IDiffResponse | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<IIdentifiableStaticData | undefined>(undefined);
  const [deleteConfirmationVisible, setDeleteConfirmationVisible] = useState<boolean>(false);
  const [jsonViewerVisible, setJsonViewerVisible] = useState<boolean>(false);
  const [jsonData, setJsonData] = useState<string>("{}");
  const [jsonDiffVisible, setJsonDiffVisible] = useState<boolean>(false);
  const [diffOldData, setDiffOldData] = useState<any>(null);
  const [diffNewData, setDiffNewData] = useState<any>(null);

  const fetchItems = async () => {
    if (!selectedBranch) return;

    try {
      setIsLoading(true);

      // Fetch both items and diff data
      const [itemsResponse, diffResponse] = await Promise.all([
        gameConfigService.listItems(selectedBranch),
        gameConfigService.getDiff(selectedBranch),
      ]);

      setItems(itemsResponse);
      setDiffData(diffResponse);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to fetch items.");
      setAlertSeverity("error");
      setItems([]);
      setDiffData(undefined);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranch]);

  const handleRefresh = () => {
    fetchItems();
  };

  const handleViewClick = (item: IIdentifiableStaticData, diffItem?: IDiffItem) => {
    try {
      if (diffItem && diffItem.oldContent && diffItem.newContent) {
        // Show diff view
        setDiffOldData({
          title: `${item.id} (Original)`,
          content: JSON.parse(diffItem.oldContent),
        });
        setDiffNewData({
          title: `${item.id} (Updated)`,
          content: JSON.parse(diffItem.newContent),
        });
        setJsonDiffVisible(true);
      } else {
        // Show regular JSON view
        setJsonData(JSON.stringify(item.data, null, 2));
        setJsonViewerVisible(true);
      }
    } catch (_) {
      setAlertMessage("Error displaying item data.");
      setAlertSeverity("error");
    }
  };

  const handleDeleteClick = (item: IIdentifiableStaticData) => {
    setSelectedItem(item);
    setDeleteConfirmationVisible(true);
  };

  const handleDeleteConfirmation = async () => {
    if (!selectedItem || !selectedBranch) return;

    try {
      setIsLoading(true);
      const success = await gameConfigService.deleteItem(selectedBranch, selectedItem.sdId);
      if (success) {
        setAlertMessage(`Item "${selectedItem.id}" deleted successfully.`);
        setAlertSeverity("success");
        await fetchItems(); // Refresh the list
      } else {
        setAlertMessage(`Failed to delete item "${selectedItem.id}".`);
        setAlertSeverity("error");
      }
    } catch (e: any) {
      setAlertMessage(e.debugMessage || `Failed to delete item "${selectedItem.id}".`);
      setAlertSeverity("error");
    } finally {
      setIsLoading(false);
      setDeleteConfirmationVisible(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmationVisible(false);
  };

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  if (!selectedBranch) {
    return (
      <Paper sx={{ p: 2, height: "70vh", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <Typography variant="subtitle1" color="textSecondary">
          Please select a branch to manage items.
        </Typography>
      </Paper>
    );
  }

  return (
    <>
      <GameConfigItemsTable
        items={items}
        diffData={diffData}
        onViewMenuClick={handleViewClick}
        onDeleteMenuClick={handleDeleteClick}
        onRefreshButtonClick={handleRefresh}
      />

      <YesNoCancelPopup
        visible={deleteConfirmationVisible}
        title="Delete Item"
        message={`Are you sure you want to delete the item <b>${selectedItem?.id}</b> (SDId: <b>${selectedItem?.sdId}</b>)?`}
        onNo={handleDeleteCancel}
        onYes={handleDeleteConfirmation}
      />

      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>

      <JsonViewer visible={jsonViewerVisible} data={jsonData} onClose={() => setJsonViewerVisible(false)} />

      <JSONDiff
        visible={jsonDiffVisible}
        oldData={diffOldData}
        newData={diffNewData}
        onClose={() => setJsonDiffVisible(false)}
      />

      {isLoading && <LoadingOverlay />}
    </>
  );
};
